import { useEffect, useRef, useState } from 'react'
import { Loader2, Route as RouteIcon, X } from 'lucide-react'

import { useRoadbook } from '../../data/roadbookContext'
import { hours, num } from '../../lib/format'
import { activeStages, routeComparison } from '../../lib/trip'
import { useFetchRoute } from './routeApi'
import {
  computeRoutes,
  stagesNeedingRoute,
  type RouteProgress,
  type RouteRunResult,
} from './stageRouting'

/**
 * Alle fehlenden Strecken auf einmal holen - und daneben zeigen, wie weit
 * Planung und Berechnung auseinanderliegen.
 *
 * Sechzehn Etappen einzeln anzuklicken ist nichts, was man von jemandem
 * verlangt. Der Server drosselt auf eine Anfrage je Sekunde, der Lauf braucht
 * also eine Weile: deshalb ein Fortschritt, der die laufende Etappe nennt, und
 * ein Abbruch, der sofort greift. Gespeichert wird nach jeder Etappe, ein
 * Abbruch verliert deshalb nichts.
 *
 * Der Vergleich steht hier und nicht im Kopf der App. Dort stehen die Werte der
 * Reise, und die sind die Handwerte - der Vergleich ist ein Werkzeug der
 * Planung und gehört dorthin, wo die Strecken entstehen.
 */
export function StageRoutesPanel() {
  const { trip, setStage } = useRoadbook()
  const fetchRoute = useFetchRoute()

  const [lauf, setLauf] = useState<RouteProgress | null>(null)
  const [meldung, setMeldung] = useState<string | null>(null)
  const [fehler, setFehler] = useState<string | null>(null)
  const abbruch = useRef<AbortController | null>(null)

  useEffect(
    () => () => {
      abbruch.current?.abort()
    },
    [],
  )

  const etappen = activeStages(trip)
  const vergleich = routeComparison(etappen)
  const offen = stagesNeedingRoute(etappen)
  const fahrten = vergleich.routed + vergleich.stale + vergleich.missing

  const starten = async () => {
    setFehler(null)
    setMeldung(null)

    // Momentaufnahme: während des Laufs ändert sich die Liste mit jeder
    // gespeicherten Strecke, und ein Lauf, der seine eigene Arbeitsliste
    // untersich weg verändert, lässt sich nicht mehr zählen.
    const zuTun = offen
    if (zuTun.length === 0) return

    const controller = new AbortController()
    abbruch.current = controller
    setLauf({ total: zuTun.length, done: 0, failed: 0, current: '' })

    const ergebnis = await computeRoutes(zuTun, {
      fetchRoute,
      save: (id, route) => {
        setStage(id, { route })
      },
      onProgress: setLauf,
      signal: controller.signal,
    })

    if (abbruch.current === controller) abbruch.current = null
    setLauf(null)
    setFehler(ergebnis.error)
    setMeldung(abschlussText(ergebnis))
  }

  const laeuft = lauf !== null
  const erledigt = lauf ? lauf.done + lauf.failed : 0
  const prozent = lauf && lauf.total > 0 ? Math.round((erledigt / lauf.total) * 100) : 0
  // Gezählt wird die Etappe, die gerade laeuft, nicht die letzte fertige -
  // beim letzten Durchgang ist das die Gesamtzahl und nicht eine mehr.
  const aktuell = lauf ? Math.min(erledigt + 1, lauf.total) : 0

  return (
    <section className="strecken kein-druck">
      <h4 className="block-titel">
        <RouteIcon size={13} /> Strecken
      </h4>

      <p className="strecken-text">
        {fahrten === 0 ? (
          'Keine Etappe mit einer Fahrt.'
        ) : (
          <>
            <b>
              {vergleich.routed} von {fahrten}
            </b>{' '}
            Fahretappen haben eine berechnete Strecke.
            {vergleich.stale > 0 &&
              ` ${String(vergleich.stale)} veraltet, weil Koordinaten oder Zwischenpunkte sich geändert haben.`}
            {vergleich.missing > 0 && ` ${String(vergleich.missing)} noch offen.`}
          </>
        )}
      </p>

      {vergleich.routed > 0 && (
        <p className="strecken-zahlen">
          Über diese {vergleich.routed}: <b>{num(vergleich.routedKm)} km</b> berechnet gegenüber{' '}
          {num(vergleich.plannedKm)} km Planung ({signed(vergleich.routedKm - vergleich.plannedKm)}{' '}
          km) · <b>{hours(vergleich.routedHours)} h</b> Fahrzeit laut Dienst gegenüber{' '}
          {hours(vergleich.plannedHours)} h Planung (
          {signedHours(vergleich.routedHours - vergleich.plannedHours)} h)
        </p>
      )}

      <div className="knopfreihe">
        <button
          type="button"
          className="knopf"
          disabled={laeuft || offen.length === 0}
          onClick={() => void starten()}
        >
          {laeuft ? <Loader2 size={14} className="dreht" /> : <RouteIcon size={14} />}
          {laeuft
            ? 'Strecken werden berechnet …'
            : offen.length === 0
              ? 'Alle Strecken berechnet'
              : `${String(offen.length)} fehlende Strecken berechnen`}
        </button>
        {laeuft && (
          <button
            type="button"
            className="knopf still"
            onClick={() => {
              abbruch.current?.abort()
            }}
          >
            <X size={14} /> Abbrechen
          </button>
        )}
      </div>

      {lauf && (
        <div className="karte-fortschritt">
          <div
            className="karte-balken"
            role="progressbar"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={prozent}
            aria-label="Fortschritt der Streckenberechnung"
          >
            <span style={{ width: `${String(prozent)}%` }} />
          </div>
          <span className="fortschritt">
            Etappe {aktuell} von {lauf.total}
            {lauf.current && ` · ${lauf.current}`}
          </span>
        </div>
      )}

      {meldung && <p className="strecken-text">{meldung}</p>}
      {fehler && <p className="karte-warnung">{fehler}</p>}

      <p className="abschluss">
        Berechnet wird einmal und dann gespeichert: unterwegs im Funkloch ist die Strecke damit da,
        auf der Karte und im Ausdruck. Der Server lässt eine Anfrage je Sekunde durch, deshalb
        dauert ein Durchgang über alle Etappen etwa eine halbe Minute.
      </p>
    </section>
  )
}

/** Was nach einem Lauf dasteht. */
function abschlussText(ergebnis: RouteRunResult): string {
  const berechnet = `${String(ergebnis.done)} ${ergebnis.done === 1 ? 'Strecke' : 'Strecken'} berechnet und gespeichert.`
  const ohne = ergebnis.failed > 0 ? ` ${String(ergebnis.failed)} ohne Ergebnis vom Dienst.` : ''

  if (ergebnis.aborted) return `Abgebrochen. ${berechnet}${ohne}`
  if (ergebnis.error) return `Abgebrochen: ${berechnet}${ohne}`
  return `${berechnet}${ohne}`
}

/** Abweichung mit Vorzeichen, etwa "+140". */
function signed(value: number): string {
  const gerundet = Math.round(value)
  if (gerundet === 0) return '±0'
  return `${gerundet > 0 ? '+' : '−'}${num(Math.abs(gerundet))}`
}

/** Dasselbe für Stunden, mit einer Nachkommastelle. */
function signedHours(value: number): string {
  const gerundet = Math.round(value * 10) / 10
  if (gerundet === 0) return '±0'
  return `${gerundet > 0 ? '+' : '−'}${hours(Math.abs(gerundet))}`
}
